import Color from '@/Enums/Color.enum';
import { addAlphaToColor } from '@/helpers/colors';
import { createTheme, inputBaseClasses } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { GREY, PRIMARY } from '../constants/color';

export const AUTH_THEME_TYPE = 'AUTH_THEME';
export const MAIN_THEME_TYPE = 'MAIN_THEME';

export const MAIN_THEME = createTheme({
  palette: {
    primary: {
      main: PRIMARY,
    },
  },
  typography: {
    fontFamily: 'Plus Jakarta Sans',
    button: {
      textTransform: 'none',
    },
  },
  components: {
    Sidebar: {
      styleOverrides: {
        root: {
          backgroundColor: '#15090C',
        },
        link: {
          color: GREY,
          '&.active': {
            backgroundColor: 'rgba(255, 255, 255, .1)',
            color: 'white',
          },
        },
        routeGroup: {
          color: GREY,
        },
      },
    },
    LoginBanner: {
      styleOverrides: {
        carousel: {
          '& .carousel-text': {
            color: GREY,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderColor: '#E4E4E4',
          [`&.${inputBaseClasses.adornedStart} > svg`]: {
            color: '#9A9AAB',
          },
          [`&.${inputBaseClasses.adornedEnd} > svg`]: {
            color: '#9A9AAB',
          },
        },
        input: {
          '::placeholder': {
            color: '#000929',
            opacity: 0.5,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        input: {
          padding: '12px 16px',
        },
        notchedOutline: {
          border: '1px solid #E3E3E3',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: 'transparent',
        },
        rail: {
          backgroundColor: '#9CA3AF',
        },
        track: {
          color: 'black',
        },
        thumb: {
          backgroundColor: '#141718',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: 15,
          fontSize: 12,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: (
          <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <rect width='16' height='16' rx='4' fill='white' />
            <rect x='1' y='1' width='14' height='14' rx='3' stroke='#000A26' strokeOpacity='0.7' />
          </svg>
        ),
        checkedIcon: (
          <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <rect width='16' height='16' rx='4' fill='currentColor' />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M11.7 5.3C11.3 4.9 10.7 4.9 10.3 5.3L7 8.6L5.7 7.3C5.3 6.9 4.7 6.9 4.3 7.3C3.9 7.7 3.9 8.3 4.3 8.7L6.3 10.7C6.5 10.9 6.7 11 7 11C7.3 11 7.5 10.9 7.7 10.7L11.7 6.7C12.1 6.3 12.1 5.7 11.7 5.3Z'
              fill='white'
              className='check-mark'
            />
          </svg>
        ),
        indeterminateIcon: (
          <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <rect width='16' height='16' rx='4' fill='currentColor' />
            <path
              d='M3.70144 7C3.30576 7 3 7.4359 3 8C3 8.5641 3.30576 9 3.70144 9H12.2986C12.6942 9 13 8.5641 13 8C13 7.4359 12.6942 7 12.2986 7H7.29856H3.70144Z'
              fill='white'
              className='indeterminate-mark'
            />
          </svg>
        ),
      },
    },
    MuiChip: {
      styleOverrides: {
        outlined: {
          borderColor: '#E4E4E4',
        },
      },
      defaultProps: {
        deleteIcon: (
          <svg
            className='delete-icon'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path d='M4.46409 11.5354L11.5352 4.46436' stroke='#84818A' strokeWidth='1.5' strokeLinecap='round' />
            <path d='M4.46409 4.46458L11.5352 11.5356' stroke='#84818A' strokeWidth='1.5' strokeLinecap='round' />
          </svg>
        ),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontWeight: 500,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: 'none',
          fontWeight: 600,
        },
        outlined: {
          borderColor: '#E7E7E7',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          fontWeight: 500,
          padding: '12px 16px',
          '& .MuiOutlinedInput-input': {
            padding: 0,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#000929',
          fontWeight: 500,
          fontSize: 14,
          display: 'block',
          marginBottom: '8px',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: '#000929',
          fontWeight: 500,
          fontSize: 14,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 23px 44px rgba(176, 183, 195, 0.14)',
          '@media print': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: '8px',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          border: '1px solid #E3E3E3',
          borderBottom: 0,
        },
      },
    },
  },
});

export const getCurrentTheme = (whitelabel, type) => theme => {
  let whitelabelTheme = {};
  const { sidebarBg, sidebarTextColor, loginBg, loginTextDark, buttonBg } = whitelabel;
  const sidebarActiveLinkColor = addAlphaToColor(sidebarTextColor, 0.2);
  const dividerColor = addAlphaToColor(sidebarTextColor, 0.1);

  switch (type) {
    case AUTH_THEME_TYPE:
      whitelabelTheme = {
        palette: {
          primary: {
            main: buttonBg || PRIMARY,
            dark: buttonBg || PRIMARY,
          },
        },
        components: {
          LoginBanner: {
            styleOverrides: {
              root: {
                background: loginBg,
              },
              carousel: {
                color: loginTextDark ? Color.DarkBlue : 'white',
                '& .carousel-text': {
                  color: loginTextDark ? Color.DarkBlue : 'white',
                },
                '& .MuiMobileStepper-dotActive': {
                  backgroundColor: loginTextDark ? Color.DarkBlue : 'white',
                },
              },
            },
          },
        },
      };
      if (loginTextDark) {
        whitelabelTheme.components = {
          ...whitelabelTheme.components,
          MuiButton: {
            styleOverrides: {
              contained: {
                color: Color.DarkBlue,
              },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: {
                color: Color.DarkBlue,
              },
            },
          },
        };
      }
      break;
    case MAIN_THEME_TYPE:
      whitelabelTheme = {
        components: {
          Sidebar: {
            styleOverrides: {
              root: {
                background: sidebarBg,
              },
              routeGroup: {
                color: sidebarTextColor,
              },
              link: {
                color: sidebarTextColor,
                '&.active': {
                  backgroundColor: sidebarActiveLinkColor,
                  color: sidebarTextColor,
                },
              },
              divider: {
                borderColor: dividerColor,
              },
            },
          },
        },
      };
      break;
  }
  return createTheme(deepmerge(theme, whitelabelTheme));
};
